import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/context/AuthContext';
import { apiFetch } from '../../shared/lib/apiClient';
import logo from '../../shared/assets/logos/ARBCOR_Text_only.png';

const ease = 'cubic-bezier(0.16,1,0.3,1)';

const Invite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, signUp, signIn, fetchCompanies, isAuthenticated } = useAuth();

  const [loaded, setLoaded] = useState(false);
  const [invite, setInvite] = useState(null); // { companyName, companyId, role }
  const [resolving, setResolving] = useState(true);
  const [resolveError, setResolveError] = useState('');

  // Signup form (for unauthenticated users)
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [error, setError] = useState('');
  const [joining, setJoining] = useState(false);
  const [success, setSuccess] = useState(false);

  const loggedIn = user && isAuthenticated();

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  // Resolve invite on mount
  useEffect(() => {
    if (!token) {
      setResolving(false);
      setResolveError('No invite code provided.');
      return;
    }

    async function resolve() {
      try {
        const res = await apiFetch(`/api/invites/resolve/${encodeURIComponent(token)}`);
        const data = await res.json();
        if (!res.ok) {
          setResolveError(data.error || 'Invalid invite code');
        } else {
          setInvite(data);
        }
      } catch {
        setResolveError('Failed to load invite. Please try again.');
      }
      setResolving(false);
    }

    // If user is logged in, resolve immediately; otherwise resolve without auth
    // The resolve endpoint requires auth, so if not logged in we'll resolve after signup
    if (loggedIn) {
      resolve();
    } else {
      // Can't resolve without auth — just show signup form, resolve after login
      setResolving(false);
    }
  }, [token, loggedIn]);

  const redeemInvite = async () => {
    const res = await apiFetch('/api/invites/redeem', {
      method: 'POST',
      body: JSON.stringify({ code: token }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to join');
    if (data.companyId) {
      localStorage.setItem('active_company_id', data.companyId);
    }
    await fetchCompanies();
    return data;
  };

  // Logged-in user clicks "Join"
  const handleJoin = async () => {
    setError('');
    setJoining(true);
    try {
      await redeemInvite();
      setSuccess(true);
      setTimeout(() => navigate('/dashboard'), 1200);
    } catch (err) {
      setError(err.message);
      setJoining(false);
    }
  };

  // New user signs up, then we redeem the invite
  const handleSignupAndJoin = async (e) => {
    e.preventDefault();
    if (!fullName || !email || !password) {
      setError('Please fill in all fields.');
      return;
    }
    setError('');
    setJoining(true);

    try {
      await signUp(email, password, fullName);
      await signIn(email, password, fullName);
      // After sign-in, redeem the invite
      await redeemInvite();
      setSuccess(true);
      setTimeout(() => navigate('/dashboard'), 1200);
    } catch (err) {
      const msg = typeof err?.description === 'object'
        ? err.description.message || 'Signup failed'
        : err?.description || err?.message || 'Failed to create account';
      setError(msg);
      setJoining(false);
    }
  };

  const clearError = () => { if (error) setError(''); };

  const fadeStyle = (delay = 0) => ({
    opacity: loaded ? 1 : 0,
    transform: loaded ? 'translateY(0)' : 'translateY(12px)',
    transition: `all 0.7s ${ease}`,
    transitionDelay: `${delay}ms`,
  });

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-12" style={fadeStyle(100)}>
          <Link to="/">
            <img
              src={logo}
              alt="Arbcor"
              className="h-5 w-auto select-none mx-auto"
              draggable="false"
            />
          </Link>
        </div>

        {resolving ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-zinc-200 border-t-zinc-600 rounded-full animate-spin" />
          </div>
        ) : resolveError && loggedIn ? (
          <div className="text-center" style={fadeStyle(200)}>
            <p className="text-sm text-red-400 mb-4">{resolveError}</p>
            <Link to="/dashboard" className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors">
              Go to Dashboard &rarr;
            </Link>
          </div>
        ) : success ? (
          <div className="text-center" style={fadeStyle(0)}>
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
              <svg
                width="22" height="22" viewBox="0 0 20 20" fill="none" stroke="currentColor"
                strokeWidth="2" className="text-emerald-600" strokeLinecap="round" strokeLinejoin="round"
              >
                <path d="M4 10.5L8 14.5L16 6" />
              </svg>
            </div>
            <h2 className="text-xl font-light text-zinc-900 mb-2">Welcome aboard!</h2>
            <p className="text-sm text-zinc-400">
              You've joined {invite?.companyName || 'the company'}. Redirecting...
            </p>
          </div>
        ) : loggedIn && invite ? (
          /* Logged-in user: show company info + Join button */
          <>
            <div className="text-center mb-8" style={fadeStyle(200)}>
              <p className="text-sm text-zinc-400 mb-3">You've been invited to join</p>
              <div className="inline-flex items-center gap-3 px-5 py-3 bg-zinc-50 rounded-lg border border-zinc-100">
                <div className="w-9 h-9 rounded-lg bg-zinc-900 flex items-center justify-center text-white text-xs font-medium shrink-0">
                  {invite.companyName?.charAt(0) || '?'}
                </div>
                <div className="text-left">
                  <div className="text-sm font-medium text-zinc-900">{invite.companyName}</div>
                  <div className="text-xs text-zinc-400">Role: {invite.role}</div>
                </div>
              </div>
            </div>

            {error && <p className="text-red-400 text-xs text-center mb-4">{error}</p>}

            <div style={fadeStyle(400)}>
              <button
                onClick={handleJoin}
                disabled={joining}
                className="w-full px-4 py-3 bg-zinc-900 text-white text-sm tracking-wider uppercase hover:bg-zinc-800 transition-colors duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {joining ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Joining...
                  </>
                ) : (
                  'Join Company'
                )}
              </button>
            </div>
          </>
        ) : (
          /* Not logged in: signup form */
          <>
            <div className="text-center mb-8" style={fadeStyle(200)}>
              <p className="text-sm text-zinc-400 mb-1">You've been invited to join a company</p>
              <p className="text-xs text-zinc-300">Create an account to get started</p>
            </div>

            <div
              className="h-px bg-zinc-200 mx-auto mb-8 transition-all duration-[1200ms]"
              style={{
                width: loaded ? '60px' : '0px',
                transitionDelay: '400ms',
                transitionTimingFunction: ease,
              }}
            />

            <form onSubmit={handleSignupAndJoin} className="space-y-4">
              <div style={fadeStyle(400)}>
                <label className="block text-xs uppercase tracking-[0.25em] text-zinc-400 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => { setFullName(e.target.value); clearError(); }}
                  placeholder="Jane Smith"
                  autoComplete="name"
                  className={`w-full px-4 py-3 border text-sm text-zinc-900 placeholder-zinc-300 outline-none transition-colors duration-300 ${
                    error ? 'border-red-300' : 'border-zinc-200 focus:border-zinc-900'
                  }`}
                />
              </div>

              <div style={fadeStyle(450)}>
                <label className="block text-xs uppercase tracking-[0.25em] text-zinc-400 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); clearError(); }}
                  placeholder="you@company.com"
                  autoComplete="email"
                  className={`w-full px-4 py-3 border text-sm text-zinc-900 placeholder-zinc-300 outline-none transition-colors duration-300 ${
                    error ? 'border-red-300' : 'border-zinc-200 focus:border-zinc-900'
                  }`}
                />
              </div>

              <div style={fadeStyle(500)}>
                <label className="block text-xs uppercase tracking-[0.25em] text-zinc-400 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); clearError(); }}
                  placeholder="Create a password"
                  autoComplete="new-password"
                  className={`w-full px-4 py-3 border text-sm text-zinc-900 placeholder-zinc-300 outline-none transition-colors duration-300 ${
                    error ? 'border-red-300' : 'border-zinc-200 focus:border-zinc-900'
                  }`}
                />
              </div>

              {error && <p className="text-red-400 text-xs mt-1">{error}</p>}

              <div style={fadeStyle(550)}>
                <button
                  type="submit"
                  disabled={joining}
                  className="w-full mt-2 px-4 py-3 bg-zinc-900 text-white text-sm tracking-wider uppercase hover:bg-zinc-800 transition-colors duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {joining ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Creating Account...
                    </>
                  ) : (
                    'Create Account & Join'
                  )}
                </button>
              </div>
            </form>

            <div className="mt-8 text-center" style={fadeStyle(600)}>
              <p className="text-zinc-400 text-sm">
                Already have an account?{' '}
                <Link
                  to={`/login?redirect=/invite/${token}`}
                  className="text-zinc-900 hover:text-zinc-600 transition-colors duration-300"
                >
                  Sign in
                </Link>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Invite;
